import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Store, Loader2, MapPin, Phone, Globe, BadgeCheck, Mic2,
  ExternalLink, Users, Smartphone, X, Send, Apple,
} from "lucide-react";
import { getPublicBusiness, submitVenueInquiry, getMe, getToken } from "@/business/api";
import HireBuskerForm from "@/business/components/HireBuskerForm";

const TYPE_LABELS = {
  cafe: "Café", bar: "Bar", restaurant: "Restaurant", music_venue: "Music Venue",
  karaoke: "Karaoke", festival: "Festival", retail: "Retail", hotel: "Hotel", other: "Local Business",
};
const DAY_ORDER = [
  { key: "mon", label: "Monday" }, { key: "tue", label: "Tuesday" }, { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" }, { key: "fri", label: "Friday" }, { key: "sat", label: "Saturday" }, { key: "sun", label: "Sunday" },
];
const PERFORMER_TYPES = ["Acoustic", "Band", "Solo Singer", "DJ", "Jazz", "Magician", "Comedian", "Dance", "Other"];

const DEFAULT_OG_IMAGE = "https://buskerbeat.com/og-image.jpg";

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[([^\]=]+)="?([^"\]]+)"?\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}
function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
function setJSONLD(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.id = id; el.type = "application/ld+json"; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data, null, 2);
}

function useSEO(business, username) {
  useEffect(() => {
    if (!business) return;
    const url = `https://buskerbeat.com/business/${encodeURIComponent(username)}`;
    const title = `${business.business_name} — BuskerBeat`;
    const desc = business.description || `${business.business_name} on BuskerBeat. Discover music, events, and more.`;
    const image = business.cover_photo || business.profile_photo || DEFAULT_OG_IMAGE;
    document.title = title;
    setMeta('meta[name="description"]', "content", desc);
    setLink("canonical", url);
    setMeta('meta[property="og:type"]', "content", "business.business");
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", image);
    if (business.city) setMeta('meta[property="business:contact_data:locality"]', "content", business.city);
    if (business.country) setMeta('meta[property="business:contact_data:country_name"]', "content", business.country);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", image);
    setJSONLD("buskerbeat-business-jsonld", {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: business.business_name,
      image,
      description: business.description || undefined,
      address: (business.address || business.city || business.country || business.postal_code) ? {
        "@type": "PostalAddress",
        streetAddress: business.address || undefined,
        addressLocality: business.city || undefined,
        addressCountry: business.country || undefined,
        postalCode: business.postal_code || undefined,
      } : undefined,
      geo: (business.latitude && business.longitude) ? {
        "@type": "GeoCoordinates", latitude: business.latitude, longitude: business.longitude,
      } : undefined,
      telephone: business.phone || undefined,
      url,
      sameAs: business.website ? [business.website] : undefined,
    });
  }, [business, username]);
}

function HireButton({ business, owner, ownerLoading, trigger, navigate }) {
  // Three modes:
  //   - Owner (logged in & profile belongs to me): open the shared HireBuskerForm in a Sheet
  //   - Other role / different business: explain why and offer logout/login
  //   - Anonymous: redirect to /business/login
  const [open, setOpen] = useState(false);
  const isOwner = !!owner && owner.id === business.id;

  const onClick = (e) => {
    if (ownerLoading) return; // wait until we know
    if (!owner) {
      // Anonymous — send to login
      navigate(`/business/login?next=${encodeURIComponent(`/business/${business.username}`)}`);
      return;
    }
    if (!isOwner) {
      toast.error("Sign in as this venue to post a gig from its profile.");
      return;
    }
    setOpen(true);
  };

  // Clone the trigger node to attach our handler + testid
  const clonedTrigger = trigger
    ? { ...trigger, props: { ...trigger.props, onClick } }
    : null;

  return (
    <>
      {clonedTrigger}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="public-profile-hire-sheet">
          <SheetHeader>
            <SheetTitle>Post a gig for {business.business_name}</SheetTitle>
            <SheetDescription>Local buskers will see this gig in the BuskerBeat app immediately.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <HireBuskerForm
              business={business}
              autoLoadLocations={true}
              preferredLocationId={business?.id}
              onSuccess={() => { setOpen(false); navigate("/business/dashboard/gigs"); }}
              onUnauthorized={() => { setOpen(false); navigate(`/business/login?next=${encodeURIComponent(`/business/${business.username}`)}`); }}
              submitLabel="Post gig"
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function StoreBadges({ className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <a href="https://apps.apple.com/app/buskerbeat" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-black text-white px-3 py-2 text-xs font-semibold hover:opacity-90 transition-opacity" data-testid="public-profile-app-store-badge">
        <Apple className="h-4 w-4" />
        <span className="leading-tight"><span className="block text-[9px] opacity-75">Download on the</span><span className="block text-sm">App Store</span></span>
      </a>
      <a href="https://play.google.com/store/apps/details?id=com.buskerbeat" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-black text-white px-3 py-2 text-xs font-semibold hover:opacity-90 transition-opacity" data-testid="public-profile-play-store-badge">
        <svg width="16" height="16" viewBox="0 0 512 512" aria-hidden="true" className="text-white"><path fill="currentColor" d="M325.3 234.3 104.7 33.7c-9-8.2-22.9-2.1-22.9 9.9v424.9c0 12 13.9 18 22.9 9.9l220.6-200.6c6.4-5.8 6.4-15.7 0-21.5zM374.5 304l-49.2-49.2-49.2 49.2 49.2 49.2zM119.6 53.3l210.5 191.5L155.8 419.1z"/></svg>
        <span className="leading-tight"><span className="block text-[9px] opacity-75">Get it on</span><span className="block text-sm">Google Play</span></span>
      </a>
    </div>
  );
}

function Lightbox({ src, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="public-profile-lightbox">
      <button onClick={onClose} className="absolute top-3 right-3 text-white/90 hover:text-white inline-flex items-center gap-1 rounded-full bg-black/60 px-3 py-2 text-sm" data-testid="public-profile-lightbox-close-button"><X className="h-4 w-4" />Close</button>
      <img src={src} alt="" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
    </div>
  );
}

function NotFoundView({ username }) {
  return (
    <div className="min-h-screen bg-[#F7F7F8] flex flex-col" data-testid="public-profile-404">
      <header className="h-14 px-4 sm:px-6 flex items-center justify-between border-b border-[#E5E7EB] bg-white">
        <Link to="/" className="flex items-center gap-2"><span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF6A00] text-white font-bold">B</span><span className="font-semibold tracking-tight">BuskerBeat</span></Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md text-center">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-[#FFF1E8] text-[#FF6A00] flex items-center justify-center mb-5"><Store className="h-7 w-7" /></div>
          <h1 className="text-2xl font-semibold text-[#111827]">Business not found</h1>
          <p className="mt-2 text-sm text-[#6B7280]">We couldn't find a venue with the handle <code className="bg-[#F3F4F6] px-1.5 py-0.5 rounded text-xs">{username}</code>.</p>
          <p className="mt-4 text-sm text-[#374151]">Looking for a venue? Browse on the BuskerBeat app to discover places near you.</p>
          <div className="mt-5"><StoreBadges className="justify-center" /></div>
          <div className="mt-6"><Link to="/" className="text-sm text-[#FF6A00] hover:underline" data-testid="public-profile-404-home-link">← Back to BuskerBeat</Link></div>
        </div>
      </main>
    </div>
  );
}

export default function BusinessProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [owner, setOwner] = useState(null);          // null = none / not me, object = business profile of signed-in business user
  const [ownerLoading, setOwnerLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPublicBusiness(username)
      .then((b) => { if (!cancelled) { setData(b); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err); setLoading(false); } });
    return () => { cancelled = true; };
  }, [username]);

  // If a business token is stored, fetch /me so we can show the "post gig"
  // form when the viewer is the owner of this profile. Anonymous visitors
  // skip this call entirely.
  useEffect(() => {
    if (!getToken()) { setOwner(null); return; }
    let cancelled = false;
    setOwnerLoading(true);
    getMe()
      .then((me) => { if (!cancelled) setOwner(me); })
      .catch(() => { if (!cancelled) setOwner(null); })
      .finally(() => { if (!cancelled) setOwnerLoading(false); });
    return () => { cancelled = true; };
  }, [username]);

  useSEO(data, username);

  const typeLabel = useMemo(() => (data ? (TYPE_LABELS[data.business_type] || TYPE_LABELS.other) : ""), [data]);
  const cityCountry = useMemo(() => data ? [data.city, data.country].filter(Boolean).join(", ") : "", [data]);
  const hasGeo = !!(data && Number.isFinite(data.latitude) && Number.isFinite(data.longitude) && (data.latitude || data.longitude));
  const mapsLink = hasGeo ? `https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}` : null;
  const mapEmbedUrl = hasGeo ? `https://maps.google.com/maps?q=${data.latitude},${data.longitude}&z=15&output=embed` : null;
  const deepLink = useMemo(() => `buskerbeat://business/${username}`, [username]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F8]" data-testid="public-profile-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF6A00]" />
      </div>
    );
  }
  if (error?.status === 404) return <NotFoundView username={username} />;
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F8] px-4" data-testid="public-profile-error">
        <Card className="max-w-md w-full border-[#E5E7EB] shadow-sm">
          <CardContent className="p-6 text-center">
            <h1 className="text-lg font-semibold">Couldn't load this venue</h1>
            <p className="mt-2 text-sm text-[#6B7280]">{error.message}</p>
            <Button className="mt-4 bg-[#FF6A00] hover:bg-[#E85F00] text-white" onClick={() => navigate(0)} data-testid="public-profile-retry-button">Try again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const b = data;
  const initials = (b.business_name || "B").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#F7F7F8]" data-testid="public-profile-page">
      {/* Top brand bar */}
      <header className="h-14 px-4 sm:px-6 flex items-center justify-between bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-2" data-testid="public-profile-brand-link">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF6A00] text-white font-bold">B</span>
          <span className="font-semibold tracking-tight">BuskerBeat</span>
        </Link>
        <div className="hidden sm:flex items-center gap-2">
          <a href={deepLink} className="text-sm text-[#6B7280] hover:text-[#111827]" data-testid="public-profile-header-open-app-link">Open in app</a>
          <Link to="/business/signup" className="text-sm font-medium text-[#FF6A00] hover:underline" data-testid="public-profile-header-list-venue-link">List your venue</Link>
        </div>
      </header>

      {/* Cover */}
      <section className="relative" data-testid="public-profile-cover">
        <div className="h-[180px] sm:h-[280px] lg:h-[340px] w-full overflow-hidden">
          {b.cover_photo ? (
            <img src={b.cover_photo} alt="" className="h-full w-full object-cover" data-testid="public-profile-cover-image" />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-[#FF6A00] via-[#FF8534] to-[#1A1A1A]" data-testid="public-profile-cover-fallback" />
          )}
        </div>
      </section>

      {/* Profile header card overlapping cover */}
      <section className="px-4 sm:px-6 lg:px-8 -mt-16 sm:-mt-20 relative z-10 pb-4">
        <div className="mx-auto max-w-[1100px]">
          <Card className="border-[#E5E7EB] shadow-sm overflow-hidden">
            <CardContent className="p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-5">
                <div className="flex-shrink-0 -mt-12 sm:-mt-16">
                  {b.profile_photo ? (
                    <img src={b.profile_photo} alt="" className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl object-cover border-4 border-white shadow-md bg-white" data-testid="public-profile-photo" />
                  ) : (
                    <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl bg-[#FFF1E8] text-[#FF6A00] border-4 border-white shadow-md flex items-center justify-center text-2xl font-bold" data-testid="public-profile-photo-fallback">
                      {initials || <Store className="h-10 w-10" />}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#111827] truncate" data-testid="public-profile-business-name">{b.business_name}</h1>
                    {b.verified && (
                      <span title="Verified business" className="inline-flex items-center text-[#2563EB]" data-testid="public-profile-verified-badge">
                        <BadgeCheck className="h-6 w-6 fill-current" />
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge className="bg-[#FFF1E8] text-[#FF6A00] border border-[#FFD7BF] hover:bg-[#FFF1E8]" data-testid="public-profile-type-pill">{typeLabel}</Badge>
                    {b.accepts_buskers && (
                      <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid="public-profile-accepts-buskers-badge">
                        <Mic2 className="h-3 w-3 mr-1" />Open to buskers
                      </Badge>
                    )}
                    {cityCountry && (
                      <span className="inline-flex items-center text-sm text-[#6B7280] gap-1" data-testid="public-profile-city-country"><MapPin className="h-3.5 w-3.5" />{cityCountry}</span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-[#6B7280]">
                    <span className="font-mono bg-[#F3F4F6] rounded px-2 py-1" data-testid="public-profile-url-chip">buskerbeat.com/business/{b.username}</span>
                    <span className="inline-flex items-center gap-1" data-testid="public-profile-followers"><Users className="h-3.5 w-3.5" /><span className="tabular-nums font-medium text-[#111827]">{(b.followers_count ?? 0).toLocaleString()}</span> followers</span>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="mt-5 grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3" data-testid="public-profile-actions">
                <HireButton business={b} owner={owner} ownerLoading={ownerLoading} navigate={navigate} trigger={
                  <Button className="bg-[#FF6A00] hover:bg-[#E85F00] text-white col-span-2 sm:col-span-1" data-testid="public-profile-hire-button">
                    <Mic2 className="mr-2 h-4 w-4" />Hire a Busker
                  </Button>
                } />
                {b.phone && (
                  <Button asChild variant="outline" data-testid="public-profile-contact-button"><a href={`tel:${b.phone.replace(/\s+/g, "")}`}><Phone className="mr-2 h-4 w-4" />Contact</a></Button>
                )}
                {b.website && (
                  <Button asChild variant="outline" data-testid="public-profile-website-button"><a href={b.website} target="_blank" rel="noreferrer"><Globe className="mr-2 h-4 w-4" />Visit Website</a></Button>
                )}
                <Button asChild variant="outline" data-testid="public-profile-open-app-button"><a href={deepLink}><Smartphone className="mr-2 h-4 w-4" />Open in App</a></Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <main className="px-4 sm:px-6 lg:px-8 pb-28 sm:pb-12" data-testid="public-profile-main">
        <div className="mx-auto max-w-[1100px] grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* About */}
            {b.description && (
              <Card className="border-[#E5E7EB] shadow-sm" data-testid="public-profile-about-card">
                <CardContent className="p-5 sm:p-6">
                  <h2 className="text-base font-semibold text-[#111827]">About this venue</h2>
                  <p className="mt-2 text-sm sm:text-[15px] text-[#374151] whitespace-pre-line leading-relaxed">{b.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Location */}
            {(b.address || b.city || b.country || hasGeo) && (
              <Card className="border-[#E5E7EB] shadow-sm overflow-hidden" data-testid="public-profile-location-card">
                <CardContent className="p-5 sm:p-6">
                  <h2 className="text-base font-semibold text-[#111827]">Location</h2>
                  <div className="mt-2 text-sm text-[#374151] space-y-0.5">
                    {b.address && <div>{b.address}</div>}
                    {(b.city || b.postal_code) && <div>{[b.city, b.postal_code].filter(Boolean).join(" ")}</div>}
                    {b.country && <div>{b.country}</div>}
                  </div>
                  {mapEmbedUrl && (
                    <div className="mt-4 rounded-xl overflow-hidden border border-[#E5E7EB]" data-testid="public-profile-map-wrap">
                      <iframe title={`Map showing ${b.business_name}`} src={mapEmbedUrl} className="w-full h-[280px] block" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                    </div>
                  )}
                  {mapsLink && (
                    <a href={mapsLink} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-[#FF6A00] hover:underline" data-testid="public-profile-open-in-maps-link">
                      Open in Google Maps <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Gallery */}
            {Array.isArray(b.gallery) && b.gallery.length > 0 && (
              <Card className="border-[#E5E7EB] shadow-sm" data-testid="public-profile-gallery-card">
                <CardContent className="p-5 sm:p-6">
                  <h2 className="text-base font-semibold text-[#111827]">Gallery</h2>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {b.gallery.map((src, i) => (
                      <button key={i} type="button" onClick={() => setLightbox(src)} className="relative aspect-square overflow-hidden rounded-lg border border-[#E5E7EB] bg-white group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6A00]" data-testid={`public-profile-gallery-tile-${i}`}>
                        <img src={src} alt="" className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar column */}
          <div className="space-y-6">
            {/* Hours */}
            {b.hours && typeof b.hours === "object" && Object.keys(b.hours).length > 0 && (
              <Card className="border-[#E5E7EB] shadow-sm" data-testid="public-profile-hours-card">
                <CardContent className="p-5 sm:p-6">
                  <h2 className="text-base font-semibold text-[#111827]">Opening hours</h2>
                  <dl className="mt-3 divide-y divide-[#E5E7EB] text-sm">
                    {DAY_ORDER.map((d) => {
                      const v = b.hours?.[d.key];
                      let text = "Closed";
                      if (typeof v === "string" && v) text = v;
                      else if (v && typeof v === "object") {
                        if (v.closed) text = "Closed";
                        else if (v.open && v.close) text = `${v.open} – ${v.close}`;
                        else text = "Closed";
                      }
                      return (
                        <div key={d.key} className="flex items-center justify-between py-2" data-testid={`public-profile-hours-${d.key}`}>
                          <dt className="text-[#374151]">{d.label}</dt>
                          <dd className="text-[#111827] font-medium tabular-nums">{text}</dd>
                        </div>
                      );
                    })}
                  </dl>
                </CardContent>
              </Card>
            )}

            {/* Get in touch */}
            <Card className="border-[#E5E7EB] shadow-sm" data-testid="public-profile-contact-card">
              <CardContent className="p-5 sm:p-6 space-y-3">
                <h2 className="text-base font-semibold text-[#111827]">Get in touch</h2>
                {b.phone && (
                  <a href={`tel:${b.phone.replace(/\s+/g, "")}`} className="flex items-center gap-3 text-sm hover:text-[#FF6A00]" data-testid="public-profile-phone-link">
                    <Phone className="h-4 w-4 text-[#6B7280]" /><span>{b.phone}</span>
                  </a>
                )}
                {b.website && (
                  <a href={b.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm hover:text-[#FF6A00] break-all" data-testid="public-profile-website-link">
                    <Globe className="h-4 w-4 text-[#6B7280] flex-shrink-0" /><span>{b.website.replace(/^https?:\/\//, "")}</span>
                  </a>
                )}
                {!b.phone && !b.website && (
                  <p className="text-xs text-[#6B7280]">This venue hasn't shared a phone or website yet. Use "Hire a Busker" to send a message instead.</p>
                )}
              </CardContent>
            </Card>

            {/* Get the app */}
            <Card className="border-[#FFD7BF] bg-[#FFF8F1] shadow-sm" data-testid="public-profile-get-app-card">
              <CardContent className="p-5 sm:p-6">
                <h3 className="text-base font-semibold text-[#111827]">Find more places like this</h3>
                <p className="mt-1 text-sm text-[#374151]">Download the BuskerBeat app to discover venues, gigs, and street performers near you.</p>
                <div className="mt-3"><StoreBadges /></div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mx-auto max-w-[1100px] mt-8 text-center text-xs text-[#6B7280]" data-testid="public-profile-footer-cta">
          Run a venue? <Link to="/business/signup" className="text-[#FF6A00] hover:underline font-medium">Sign up at /business/signup</Link>
        </div>
      </main>

      {/* Sticky mobile action bar */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[#E5E7EB] bg-white/95 backdrop-blur p-2 grid grid-cols-3 gap-2" data-testid="public-profile-mobile-action-bar">
        <HireButton business={b} owner={owner} ownerLoading={ownerLoading} navigate={navigate} trigger={
          <Button className="bg-[#FF6A00] hover:bg-[#E85F00] text-white" data-testid="public-profile-mobile-hire-button"><Mic2 className="h-4 w-4 mr-1" />Hire</Button>
        } />
        {b.phone ? (
          <Button asChild variant="outline" data-testid="public-profile-mobile-contact-button"><a href={`tel:${b.phone.replace(/\s+/g, "")}`}><Phone className="h-4 w-4 mr-1" />Call</a></Button>
        ) : (
          <Button variant="outline" disabled data-testid="public-profile-mobile-contact-button"><Phone className="h-4 w-4 mr-1" />Call</Button>
        )}
        <Button asChild variant="outline" data-testid="public-profile-mobile-open-app-button"><a href={deepLink}><Smartphone className="h-4 w-4 mr-1" />App</a></Button>
      </div>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
